import React, { useEffect, useMemo, useState } from 'react';
import planningEchangesApi from '../../services/planning-echanges';
import { ICONE_CANAL, LIBELLE_CANAL, RESULTATS, libelleQuand } from '../../utils/echanges';
import './DerniersContacts.css';
import './ClientsSansContact.css';

// Clients sans contact depuis N jours — Planning ▸ Tableaux.
//
// Mêmes dossiers que les Derniers contacts (non restitués, une ligne par OR),
// mais seulement ceux dont le dernier échange SORTANT de l'onglet Échanges
// date d'au moins N jours, comptés en jours locaux Antilles par le serveur.
// Les échanges entrants (le client qui appelle ou répond) ne comptent pas :
// un dossier qui n'a que ceux-là est « jamais contacté ». Les dossiers sans
// échange sortant sont inclus par défaut, en tête, et comptés à part.
//
// ⚠️ Fermé au technicien : le composant n'est pas monté pour lui, et le serveur
// refuse la route de toute façon.
// Date du RDV au planning (heure des Antilles). Défaut : depuis le 1er janvier 2026.
const PERIODES = [
  { id: '1m', label: '1 mois' },
  { id: '3m', label: '3 mois' },
  { id: '6m', label: '6 mois' },
  { id: '9m', label: '9 mois' },
  { id: '2026', label: 'Depuis le 1er janvier 2026' },
];
const MODES = [
  { id: 'tous', label: 'Avec les jamais contactés' },
  { id: 'contactes', label: 'Déjà contactés seulement' },
  { id: 'jamais', label: 'Jamais contactés seulement' },
];
const LIBELLE_RESULTAT = Object.fromEntries(RESULTATS.map(r => [r.id, r.label]));

export default function ClientsSansContact({ onOuvrirDossier }) {
  const [saisieN, setSaisieN] = useState('7');
  const [n, setN] = useState(7);
  const [periode, setPeriode] = useState('2026');
  const [mode, setMode] = useState('tous');
  const [data, setData] = useState({ dossiers: [], total: 0, nb_jamais: 0, plafond: 500 });
  const [chargement, setChargement] = useState(true);
  const [recherche, setRecherche] = useState('');
  const [erreur, setErreur] = useState('');
  const [exportEnCours, setExportEnCours] = useState(false);

  // N saisi au clavier : on attend une courte pause avant de relancer la requête.
  useEffect(() => {
    const v = parseInt(saisieN, 10);
    if (!Number.isFinite(v) || v < 0) return undefined;
    const t = setTimeout(() => setN(v), 400);
    return () => clearTimeout(t);
  }, [saisieN]);

  useEffect(() => {
    let vivant = true;
    setChargement(true); setErreur('');
    planningEchangesApi.sansContact({ n, periode, mode })
      .then(r => { if (vivant) setData(r.data); })
      .catch(e => { if (vivant) setErreur(e.response?.data?.error || 'Chargement impossible'); })
      .finally(() => { if (vivant) setChargement(false); });
    return () => { vivant = false; };
  }, [n, periode, mode]);

  const lignes = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    if (!q) return data.dossiers;
    return data.dossiers.filter(d =>
      [d.client_nom, d.client_prenom, d.immatriculation, d.numero_or, d.marque, d.statut_nom]
        .some(v => String(v || '').toLowerCase().includes(q)));
  }, [data.dossiers, recherche]);

  const exporterCsv = async () => {
    setExportEnCours(true);
    try {
      const r = await planningEchangesApi.sansContactCsv({ n, periode, mode });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `clients_sans_contact_${n}j.csv`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setErreur('Export impossible');
    } finally {
      setExportEnCours(false);
    }
  };

  const contactes = data.total - data.nb_jamais;

  return (
    <div className="dc">
      <div className="csc-entete">
        <div>
          <h2>🔕 Clients sans contact</h2>
          <p className="csc-sous">
            Dossiers non restitués dont le dernier échange <b>sortant</b> (onglet Échanges du dossier) date d'au moins N jours,
            en jours calendaires, heure des Antilles. Les appels et messages reçus du client ne comptent pas.
          </p>
        </div>
        <button type="button" className="csc-export" onClick={exporterCsv} disabled={exportEnCours || chargement || data.total === 0}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          {exportEnCours ? 'Export…' : 'CSV'}
        </button>
      </div>

      <div className="page-toolbar">
        <input
          className="page-search"
          placeholder="🔍 Rechercher..."
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
        />
        <label className="csc-n" title="Nombre de jours sans contact (aujourd'hui − date du dernier échange)">
          Sans contact depuis
          <input
            type="number" min="0" max="3650" step="1"
            className="page-select csc-n-input"
            value={saisieN}
            onChange={(e) => setSaisieN(e.target.value)}
          />
          jours
        </label>
        <select className="page-select" value={mode} onChange={(e) => setMode(e.target.value)}>
          {MODES.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
        </select>
        <select className="page-select" value={periode} onChange={(e) => setPeriode(e.target.value)} title="Date du RDV au planning (RDV à venir compris)">
          {PERIODES.map(p => <option key={p.id} value={p.id}>RDV : {p.label}</option>)}
        </select>
        <span className="dc-resume">
          {chargement ? 'Chargement…' : (
            <>
              <b>{data.total}</b> dossier{data.total > 1 ? 's' : ''}
              {mode !== 'jamais' && <> · <b>{contactes}</b> sans contact depuis {n} j ou plus</>}
              {mode !== 'contactes' && <> · <b className="dc-alerte">{data.nb_jamais}</b> jamais contacté{data.nb_jamais > 1 ? 's' : ''}</>}
            </>
          )}
        </span>
      </div>

      {!chargement && data.total > data.dossiers.length && (
        <div className="dc-plafond">
          Les <b>{data.dossiers.length}</b> dossiers au silence le plus long, sur {data.total}.
          Réduisez la période, augmentez N ou exportez en CSV (liste complète).
        </div>
      )}

      {erreur && <div className="dc-erreur">{erreur}</div>}

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Jours sans contact</th>
              <th>Client</th>
              <th>Téléphone</th>
              <th>Immat.</th>
              <th>Marque</th>
              <th>Statut atelier</th>
              <th>Dernier échange sortant</th>
              <th>Type · auteur</th>
            </tr>
          </thead>
          <tbody>
            {!chargement && lignes.length === 0 && (
              <tr><td colSpan={8} className="dc-vide">Aucun dossier à afficher.</td></tr>
            )}
            {lignes.map(d => (
              <tr
                key={d.cle}
                className="dc-ligne"
                onClick={() => onOuvrirDossier && onOuvrirDossier(d.intervention_id)}
                title="Ouvrir le dossier"
              >
                <td>
                  <span className="dc-pastille dc-pastille--alerte">
                    {d.dernier_contact ? `${d.jours_sans_contact} j` : 'Jamais contacté'}
                  </span>
                  {d.a_rappeler_le && (
                    <span className="dc-rappel" title="Rappel promis au client">
                      ⏰ {new Date(d.a_rappeler_le).toLocaleDateString('fr-FR')}
                    </span>
                  )}
                </td>
                <td className="dc-client">{[d.client_nom, d.client_prenom].filter(Boolean).join(' ') || '—'}</td>
                <td>
                  {d.client_tel
                    ? <a className="dc-tel" href={`tel:${String(d.client_tel).replace(/\D/g, '')}`} onClick={(e) => e.stopPropagation()}>{d.client_tel}</a>
                    : <span className="dc-sans">—</span>}
                </td>
                <td><span className="dc-immat">{d.immatriculation || '—'}</span></td>
                <td>{d.marque || <span className="dc-sans">—</span>}</td>
                <td>
                  {d.statut_nom ? (
                    <span className="dc-statut" style={{ background: d.statut_couleur || undefined, color: d.statut_text_color || undefined }}>
                      {d.statut_nom}
                    </span>
                  ) : <span className="dc-sans">—</span>}
                </td>
                <td className="dc-canal">
                  {d.dernier_contact ? libelleQuand(d.dernier_contact) : <span className="dc-sans">—</span>}
                </td>
                <td className="dc-canal">
                  {d.dernier_contact
                    ? <>{ICONE_CANAL[d.dernier_canal] || '💬'} {LIBELLE_CANAL[d.dernier_canal] || ''}
                        {d.dernier_resultat && <span className="dc-par"> · {LIBELLE_RESULTAT[d.dernier_resultat] || d.dernier_resultat}</span>}
                        <span className="dc-par"> · {d.dernier_par || ''}</span></>
                    : <span className="dc-sans">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
